import { ArrowLeftIcon, Building2Icon, HourglassIcon, MailIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AdminUserQuotaDetailDTO } from '@shared/schemas/quota';
import { Button } from '@/components/ui/button';
import { AccountTypeBadge } from '@/features/quota/components/QuotaInconsistentBadge';

type AdminUserQuotaHeaderProps = {
  detail: AdminUserQuotaDetailDTO;
};

function formatExpirationDate(iso: string) {
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    .format(new Date(iso));
}

function isExpirationPassed(iso: string) {
  return new Date(iso).getTime() <= Date.now();
}

export function AdminUserQuotaHeader({ detail }: AdminUserQuotaHeaderProps) {
  const isDemo = detail.user.accountType === 'demo';
  const expiresAt = detail.user.demoExpiresAt;
  const isExpired = isDemo && expiresAt !== null && isExpirationPassed(expiresAt);

  return (
    <div className="space-y-5">
      <Button
        asChild
        variant="ghost"
        className="h-10 px-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
      >
        <Link to="/admin/users" data-testid="quota-back-to-users">
          <ArrowLeftIcon className="size-4" />
          Retour aux membres
        </Link>
      </Button>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">{detail.user.name}</h1>
            <AccountTypeBadge accountType={detail.user.accountType} />
          </div>
          <div className="flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
            <span className="inline-flex items-center gap-2">
              <MailIcon className="size-4" />
              {detail.user.email}
            </span>
            <span className="inline-flex items-center gap-2">
              <Building2Icon className="size-4" />
              {detail.user.companyName ?? '—'}
            </span>
            {isDemo ? (
              <span className="inline-flex items-center gap-2">
                <HourglassIcon className="size-4" />
                {expiresAt === null
                  ? 'Sans expiration'
                  : isExpired
                    ? `Expiré depuis le ${formatExpirationDate(expiresAt)}`
                    : `Expire le ${formatExpirationDate(expiresAt)}`}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
